import Hls from "hls.js";
import { RefObject, useEffect, useRef, useState } from "react";
import { trackCameraStreamError, trackCameraStreamSuccess } from "@/lib/analytics";

const STALL_TIMEOUT_MS = 8000;

export const useVideoPlayer = (
  videoRef: RefObject<HTMLVideoElement>,
  url: string,
  cameraName: string,
) => {
  const hlsRef = useRef<Hls | null>(null);
  const [showError, setShowError] = useState(false);
  const [stalled, setStalled] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    setShowError(false);
    setStalled(false);

    if (!url) return;

    // Start a stall timer — if no video data loaded within timeout, show stalled UI
    let stallTimer: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      const video = videoRef.current;
      // readyState >= 2 means metadata + some data loaded (even if paused/autoplay blocked)
      if (video && video.readyState < 2) {
        setStalled(true);
      }
    }, STALL_TIMEOUT_MS);

    const clearStallTimer = () => {
      if (stallTimer) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
      setStalled(false);
    };

    if (Hls.isSupported()) {
      const beachHls = new Hls();
      beachHls.loadSource(url);
      beachHls.attachMedia(videoRef.current as HTMLVideoElement);

      beachHls.on(Hls.Events.MANIFEST_PARSED, async () => {
        trackCameraStreamSuccess(cameraName);
        if (videoRef.current) {
          try {
            await videoRef.current.play();
            clearStallTimer();
          } catch (error) {
            // Autoplay was prevented by browser policy
          }
        }
      });

      beachHls.on(Hls.Events.ERROR, (_event, err) => {
        if (err.fatal || (err.response && err.response.code === 404)) {
          clearStallTimer();
          setShowError(true);
          trackCameraStreamError(cameraName, err.details ?? err.type ?? "unknown");
          beachHls.destroy();
          hlsRef.current = null;
        }
      });

      // Clear stall if video loads data or starts playing
      const video = videoRef.current;
      const onLoaded = () => clearStallTimer();
      video?.addEventListener("loadeddata", onLoaded);
      video?.addEventListener("playing", onLoaded);

      hlsRef.current = beachHls;

      return () => {
        clearStallTimer();
        video?.removeEventListener("loadeddata", onLoaded);
        video?.removeEventListener("playing", onLoaded);
        if (beachHls && beachHls.destroy) {
          beachHls.destroy();
        }
        hlsRef.current = null;
      };
    } else {
      // Native HLS path (Safari). Analytics events have to come from
      // the `<video>` events since hls.js isn't involved here.
      if (videoRef.current) {
        videoRef.current.src = url;
        videoRef.current.play().catch(() => {
          // Autoplay was prevented by browser policy
        });
        const video = videoRef.current;
        const onLoaded = () => {
          clearStallTimer();
          trackCameraStreamSuccess(cameraName);
        };
        const onError = () => trackCameraStreamError(cameraName, "native-hls-error");
        video.addEventListener("loadeddata", onLoaded);
        video.addEventListener("playing", onLoaded);
        video.addEventListener("error", onError);

        return () => {
          clearStallTimer();
          video.removeEventListener("loadeddata", onLoaded);
          video.removeEventListener("playing", onLoaded);
          video.removeEventListener("error", onError);
        };
      }
    }
  }, [url, videoRef, retryKey, cameraName]);

  const retry = () => {
    setRetryKey((k) => k + 1);
  };

  const cleanupVideo = () => {
    if (hlsRef.current && hlsRef.current.destroy) {
      hlsRef.current.destroy();
    }
    hlsRef.current = null;
  };

  return { showError, stalled, cleanupVideo, retry };
};
